import { useMemo } from 'react';
import { strToModality } from '~/domain/dicom/modality';
import { DatasetAdapter } from '../adapters/types';
import { Layout, ViewportId } from '../layout/types';
import {
    GridSnapshot,
    TileGeometryContext,
    windowLevelTargets,
} from './windowLevelFanout';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';

/**
 * React binding for the window/level fan-out core (`windowLevelFanout.ts`):
 * derives the per-tile frame-of-reference and modality snapshot from the
 * live layout and exposes the series a named preset chosen on one tile fans
 * out to. Cursor gestures and window/level values are not owned here: the
 * engine pipeline and the per-series event bus do; this only answers the
 * fan-out question.
 */
export type WindowLevelFanout = (
    id: ViewportId
) => ReadonlyArray<SeriesInstanceUid>;

/** Derives the fan-out-relevant context for every bound cell. */
function buildSnapshot(
    layout: Layout,
    datasetAdapter: DatasetAdapter
): GridSnapshot {
    const tiles: TileGeometryContext[] = [];
    layout.rows.forEach((row) => {
        const metadata = row.dataset
            ? datasetAdapter.resolve(row.dataset.seriesInstanceUid)?.metadata
            : undefined;
        if (!metadata) {
            return; // unbound rows have no fan-out target
        }
        row.cells.forEach((cell) => {
            tiles.push({
                id: cell.id,
                forUid: metadata.frameOfReferenceUid,
                seriesUid: metadata.seriesInstanceUid,
                modality: strToModality(metadata.modality),
            });
        });
    });
    return { tiles };
}

export function useWindowLevelFanout(
    layout: Layout,
    datasetAdapter: DatasetAdapter
): WindowLevelFanout {
    const snapshot = useMemo(
        () => buildSnapshot(layout, datasetAdapter),
        [layout, datasetAdapter]
    );
    return (id) => windowLevelTargets(snapshot, id);
}
